import {
  Building2, Users, CalendarDays, AlertTriangle, ChevronRight,
  Activity, CheckCircle2, Clock, TrendingUp,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { ActivityFeed } from "@/components/ActivityFeed";
import { useAuth } from "@/contexts/AuthContext";
import { useMyAssignments } from "@/hooks/useUserAssignments";
import { useOrgStats } from "@/hooks/useDashboardStats";
import { useBookings } from "@/hooks/useBookings";
import { useHosts } from "@/hooks/useHosts";
import { useNavigate, Link } from "react-router-dom";

// ─── Per-org stats mini card ──────────────────────────────────────────────────

function OrgCard({ assignment, isActive, onSelect }: { assignment: any; isActive: boolean; onSelect: () => void }) {
  const org = assignment.organization;
  const { data: stats, isLoading } = useOrgStats(org?.id ?? null);

  return (
    <Card
      className={`cursor-pointer transition-colors ${isActive ? "border-primary shadow-sm" : "hover:border-primary/50"}`}
      onClick={onSelect}
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Building2 className="h-4 w-4 text-primary" />
            {org?.name ?? "Organization"}
          </CardTitle>
          <div className="flex items-center gap-1.5">
            {isActive && <Badge className="text-[9px] px-1.5 py-0 bg-primary">Active</Badge>}
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>
        {org?.address && <p className="text-xs text-muted-foreground">{org.address}</p>}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="grid grid-cols-3 gap-2">
            {[0, 1, 2].map((i) => <Skeleton key={i} className="h-12" />)}
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-md bg-muted p-2">
              <p className="text-lg font-bold">{stats?.totalUnits ?? 0}</p>
              <p className="text-[10px] text-muted-foreground">Units</p>
            </div>
            <div className="rounded-md bg-muted p-2">
              <p className="text-lg font-bold">{stats?.activeHosts ?? 0}</p>
              <p className="text-[10px] text-muted-foreground">Hosts</p>
            </div>
            <div className="rounded-md bg-muted p-2">
              <p className="text-lg font-bold">{stats?.activeBookings ?? 0}</p>
              <p className="text-[10px] text-muted-foreground">Bookings</p>
            </div>
          </div>
        )}
        {(stats?.complianceAlerts ?? 0) > 0 && (
          <div className="mt-2 flex items-center gap-1 text-xs text-destructive">
            <AlertTriangle className="h-3 w-3" />
            {stats!.complianceAlerts} compliance alert{stats!.complianceAlerts > 1 ? "s" : ""}
          </div>
        )}
        {(stats?.pendingHosts ?? 0) > 0 && (
          <div className="mt-1 flex items-center gap-1 text-xs text-amber-600">
            <Users className="h-3 w-3" />
            {stats!.pendingHosts} host{stats!.pendingHosts > 1 ? "s" : ""} pending
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Today summary for the active org ────────────────────────────────────────

function TodaySummary({ orgId }: { orgId: string }) {
  const today = new Date().toISOString().split("T")[0];
  const { data: allBookings = [], isLoading: bookingsLoading } = useBookings();
  const { data: hosts = [], isLoading: hostsLoading } = useHosts();

  const checkInsToday = allBookings.filter((b: any) => b.check_in === today && b.status === "approved");
  const checkOutsToday = allBookings.filter((b: any) => b.check_out === today && b.status === "approved");
  const pendingBookings = allBookings.filter((b: any) => b.status === "pending");
  const pendingHosts = hosts.filter((h: any) => h.status === "pending");

  const isLoading = bookingsLoading || hostsLoading;

  return (
    <Card className="border-primary/20 bg-primary/5">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-primary" />
          Today — {new Date().toLocaleDateString("en-CA", { weekday: "long", month: "long", day: "numeric" })}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex gap-3">
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-14 flex-1" />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {/* Check-ins */}
            <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-center">
              <p className="text-2xl font-bold text-green-700">{checkInsToday.length}</p>
              <p className="text-[11px] text-green-600 mt-0.5">Check-ins</p>
              {checkInsToday.map((b: any) => (
                <p key={b.id} className="text-[10px] text-green-600 mt-0.5 truncate">{b.guest_name}</p>
              ))}
            </div>
            {/* Check-outs */}
            <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-center">
              <p className="text-2xl font-bold text-blue-700">{checkOutsToday.length}</p>
              <p className="text-[11px] text-blue-600 mt-0.5">Check-outs</p>
            </div>
            {/* Pending bookings */}
            <div className={`rounded-lg border p-3 text-center ${pendingBookings.length > 0 ? "bg-amber-50 border-amber-200" : "bg-muted border-border"}`}>
              <p className={`text-2xl font-bold ${pendingBookings.length > 0 ? "text-amber-700" : "text-muted-foreground"}`}>
                {pendingBookings.length}
              </p>
              <p className="text-[11px] text-muted-foreground mt-0.5">Pending Bookings</p>
            </div>
            {/* Pending hosts */}
            <div className={`rounded-lg border p-3 text-center ${pendingHosts.length > 0 ? "bg-purple-50 border-purple-200" : "bg-muted border-border"}`}>
              <p className={`text-2xl font-bold ${pendingHosts.length > 0 ? "text-purple-700" : "text-muted-foreground"}`}>
                {pendingHosts.length}
              </p>
              <p className="text-[11px] text-muted-foreground mt-0.5">Pending Hosts</p>
            </div>
          </div>
        )}

        {/* Quick actions */}
        {(pendingBookings.length > 0 || pendingHosts.length > 0) && (
          <>
            <Separator className="my-3" />
            <div className="flex flex-wrap gap-2">
              {pendingBookings.length > 0 && (
                <Button size="sm" variant="outline" className="h-7 text-xs gap-1.5" asChild>
                  <Link to="/bookings">
                    <Clock className="h-3 w-3" /> Review {pendingBookings.length} Booking{pendingBookings.length > 1 ? "s" : ""}
                  </Link>
                </Button>
              )}
              {pendingHosts.length > 0 && (
                <Button size="sm" variant="outline" className="h-7 text-xs gap-1.5" asChild>
                  <Link to="/hosts">
                    <Users className="h-3 w-3" /> Review {pendingHosts.length} Host{pendingHosts.length > 1 ? "s" : ""}
                  </Link>
                </Button>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function PropertyManagerDashboard() {
  const { user, activeOrgId, setActiveOrgId } = useAuth();
  const navigate = useNavigate();
  const { data: assignments = [], isLoading } = useMyAssignments();
  const pmAssignments = assignments.filter(
    (a) => a.role === "property_manager" || a.role === "strata_admin"
  );

  const handleSelectOrg = (a: any) => {
    setActiveOrgId(a.org_id);
    navigate("/units");
  };

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Portfolio</h1>
        <p className="text-sm text-muted-foreground">
          Welcome back, {user?.fullName?.split(" ")[0]}. You manage{" "}
          {pmAssignments.length} building{pmAssignments.length !== 1 ? "s" : ""}.
        </p>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-44 w-full" />
          <div className="grid gap-4 sm:grid-cols-2">
            {[0, 1].map((i) => <Skeleton key={i} className="h-44" />)}
          </div>
        </div>
      ) : pmAssignments.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12">
            <Building2 className="h-12 w-12 text-muted-foreground" />
            <h2 className="text-lg font-semibold">No buildings assigned yet</h2>
            <p className="text-sm text-muted-foreground text-center">
              Ask a strata council admin to invite you to their building.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* ── Today view (for the active org) ── */}
          {activeOrgId && <TodaySummary orgId={activeOrgId} />}

          {/* ── Building cards ── */}
          <div>
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
              Your Buildings
            </h2>
            <div className="grid gap-4 sm:grid-cols-2">
              {pmAssignments.map((a) => (
                <OrgCard
                  key={a.id}
                  assignment={a}
                  isActive={a.org_id === activeOrgId}
                  onSelect={() => handleSelectOrg(a)}
                />
              ))}
            </div>
          </div>

          {/* ── Activity feed for active org ── */}
          {activeOrgId && (
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Activity className="h-4 w-4 text-muted-foreground" />
                    Recent Activity
                  </CardTitle>
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => navigate("/audit-log")}>
                    View all
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <ActivityFeed
                  orgId={activeOrgId}
                  compact
                  limit={8}
                  emptyMessage="No activity yet for this building."
                />
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
